import { digger } from '@cbre/digger';
import { defaultTxt, EMPTY_STRING, simpleValueValidation } from '@miq/utils';
import { ReportRecord } from '../../../../../models/report.model';
import { DisplayFnArgumentsInfo } from '../../../../../models/template.model';

export const RoleDescriptions = {
  TenantRep: 'Tenant Representative',
};

/**
 * Format Company/Contact records into string using company name and contact
 * display name.
 *
 * @param record
 * @param attributeInfo
 * @param requiredRole
 *
 * @example
 * Expected output from this method for a single tim record
 * CompanyName1 - Contact1,Contact2,Contact3
 * CompanyName2 - Contact2,Contact3,Contact4
 */
export function formatCompanyContactByRole(
  record: ReportRecord,
  attributeInfo: DisplayFnArgumentsInfo,
  requiredRole: string
): string {
  if (!simpleValueValidation(attributeInfo.field)) {
    return defaultTxt;
  }
  const companyContactRecords = digger(record.data, attributeInfo?.field?.split('/')[0]);
  const uniqueCompanyIds = [...new Set(companyContactRecords.map(item => item.company_id))];
  const formattedCompanyContacts: string[] = uniqueCompanyIds.map((companyId, _index) => {
    // filter company/contact record by id and role
    const filteredCompanyContacts = companyContactRecords.filter(
      itemContact => itemContact.company_id === companyId && itemContact.role_desc === requiredRole
    );
    let companyName: string;
    if (filteredCompanyContacts.length) {
      companyName = filteredCompanyContacts[0].party_organization_display_name;
      // join contact display names
      const contactsDisplayName = filteredCompanyContacts
        .filter(data => data?.contact_id)
        .map(({ familiar_name }) => familiar_name ?? '-')
        .join(', ');
      const displayContactsName = contactsDisplayName ? `\n ${contactsDisplayName}` : `\n ${defaultTxt}`;
      return companyName ? `${companyName} ${displayContactsName}` : EMPTY_STRING;
    }
    return EMPTY_STRING;
  });
  return formattedCompanyContacts.length > 0
    ? formattedCompanyContacts.filter(data => data != EMPTY_STRING).join(' \n')
    : EMPTY_STRING;
}

export function formatContactByRole(record: ReportRecord, requiredRole: string): string {
  const companyContacts = digger(record.data, `company_contact_role_addresses[role_desc='${requiredRole}']`);
  if (companyContacts == null) {
    return defaultTxt;
  }
  const arrayCompanyContacts = Array.isArray(companyContacts) ? companyContacts : [companyContacts];
  return (
    arrayCompanyContacts
      .filter(item => item.contact_id)
      .map(({ familiar_name }) => familiar_name ?? '-')
      .join(', ') ?? defaultTxt
  );
}
